import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SEO from '@/components/base/SEO';
import { categories, articles } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find(c => c.slug === slug);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;

  useEffect(() => {
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4 block"></i>
            <h2 className="text-xl font-bold text-foreground-800 mb-2">分类未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">您访问的分类不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const catArticles = articles.filter(a => a.category === slug);
  const totalPages = Math.ceil(catArticles.length / pageSize);
  const paginatedArticles = catArticles.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const isAccent = category.color === 'accent';
  const bgClass = isAccent ? 'bg-accent-500' : 'bg-primary-500';
  const lightBgClass = isAccent ? 'bg-accent-100' : 'bg-primary-100';
  const textClass = isAccent ? 'text-accent-600' : 'text-primary-600';

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 精品一区二区`,
    description: category.description,
    url: `https://jingpinyiquercu.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '精品一区二区',
      url: 'https://jingpinyiquercu.com/',
    },
  };

  return (
    <>
      <SEO
        title={`${category.name} - 精品一区二区·优选内容聚合平台`}
        description={category.description}
        keywords={`${category.name},精品分区内容,优质分区资源,精品内容聚合,${category.name}文章`}
        canonicalUrl={`https://jingpinyiquercu.com/category/${slug}`}
        schemaData={schemaData}
      />

      <Navbar />

      <main>
        <section className={`relative pt-24 pb-14 md:pt-28 md:pb-16 ${lightBgClass}`}>
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="mb-4">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category.name },
                ]}
              />
            </div>
            <div className="flex items-center gap-4 mt-6">
              <div className={`w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl ${isAccent ? 'bg-accent-500/20' : 'bg-primary-500/20'}`}>
                <i className={`${category.icon} text-2xl ${textClass}`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1 leading-relaxed">
                  {category.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <p className="text-sm text-foreground-500 whitespace-nowrap">
                共 <strong className="text-foreground-800">{catArticles.length}</strong> 篇文章
              </p>
            </div>

            {paginatedArticles.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {paginatedArticles.map(article => (
                    <ArticleCard key={article.id} {...article} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-12">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }).map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i + 1)}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                          currentPage === i + 1
                            ? isAccent
                              ? 'bg-accent-500 text-background-50'
                              : 'bg-primary-500 text-background-50'
                            : 'border border-background-200 text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}